import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Col} from "reactstrap";
import {matchPath} from "react-router";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import FreeShippingFeesManagementViewSettings from "./sections/settings";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import FreeShippingFeesManagementViewDetachedCustomers from "./sections/detached-customers";
import Api from "../../../../../../core/services/api/api";
import ApiResponseCodes from "../../../../../../core/models/static/api-response-codes";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import {useLocation} from "react-router-dom";
import {useAppSelector} from "../../../../../redux";

const tabs = {
    settings: 'settings',
    detachedCustomers: 'detachedCustomers',
}

const tabsList = [
    {value: tabs.settings, label: "Settings"},
    {value: tabs.detachedCustomers, label: "Detached Customers"},
];

const FreeShippingFeesManagementView = () => {
    const location = useLocation();
    const [tab, setTab] = useState(tabs.settings);
    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const [loading, setLoading] = useState(!!defaultCustomer);
    const [customerDetached, setCustomerDetached] = useState(false);
    const [customerExceptionId, setCustomerExceptionId] = useState();
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('shipping-fees-settings-view');

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     */
    useEffect(() => {
        if (defaultCustomer?.code) {
            configureNotebookTags((noteTags) => noteTags.customerShippingFeesSettings(defaultCustomer.code));
        } else {
            configureNotebookTags((noteTags) => noteTags.shippingFeesSettings);
        }
    }, [configureNotebookTags, defaultCustomer]);

    const inCustomerPanel = useMemo(() => !!matchPath(location.pathname, {path: routes.customer.base, exact: false}), [location])

    /**
     * With each change in the [inCustomerPanel] memo value:
     * - sets the tab to the settings tab if the user is in the users panel.
     */
    useEffect(() => {
        if (inCustomerPanel) {
            setTab(tabs.settings);
        }
    }, [inCustomerPanel])

    /**
     * Determines whether the customer associated with the selected user panel is detached from the free shipping system rules.
     * @type {(function(): Promise<void|undefined>)|*}
     */
    const determineIfCustomerIsDetached = useCallback(async () => {
        if (!defaultCustomer?.id) {
            setLoading(false);
            return setCustomerDetached(false);
        }
        setLoading(true);
        const response = await Api.getFreightCustomerExceptionInfo(defaultCustomer?.id);
        if (!isMounted())
            return;
        setLoading(false);
        if (response.errorCode === ApiResponseCodes.freeFreightCustomerNotDetached) {
            return setCustomerDetached(false);
        } else {
            setCustomerDetached(true);
            setCustomerExceptionId(response.data.id);
        }
    }, [defaultCustomer?.id, isMounted])

    /**
     * With each change in the customer information:
     * - determines whether the customer associated with the selected user panel is detached from the free shipping system rules.
     */
    useEffect(() => {
        determineIfCustomerIsDetached().then();
    }, [determineIfCustomerIsDetached])

    /**
     * Reattaches the current customer to the system rules of the free shipping management feature
     * @type {(function(): Promise<void>)}
     */
    const reattachCustomerToSystemRules = useCallback(async () => {
        if (!customerExceptionId)
            return;
        const response = await Api.removeFreightCustomerException(customerExceptionId)
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCustomerDetached(false);
            setCustomerExceptionId(null);
        }
    }, [customerExceptionId, isMounted])

    /**
     * Detaches the customer to the system rules of the free shipping management feature.
     * @type {(function(): Promise<void>)}
     */
    const detachCustomerFromSystemRules = useCallback(async () => {
        const response = await Api.makeFreightCustomerException({
            customerOriginalId: defaultCustomer?.id,
            customerTitle: defaultCustomer?.name,
        })
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCustomerDetached(true);
        }
    }, [defaultCustomer?.id, defaultCustomer?.name, isMounted])

    /**
     * Changes the value of the tab state
     * @param e
     * @param newTab
     */
    const onTabChanged = useCallback((e, newTab) => {
        setTab(newTab);
    }, [])

    /**@type {ReactNode}*/
    const content = useMemo(() => {
        switch (tab) {
            case tabs.settings:
                return <FreeShippingFeesManagementViewSettings
                    inUsersPanel={inCustomerPanel}
                    customer={defaultCustomer}
                    customerDetached={customerDetached}
                    reattachCustomerToSystemRules={reattachCustomerToSystemRules}
                    detachCustomerFromSystemRules={detachCustomerFromSystemRules}
                />
            case tabs.detachedCustomers:
                return <FreeShippingFeesManagementViewDetachedCustomers/>
            default:
                return null;
        }
    }, [defaultCustomer, customerDetached, detachCustomerFromSystemRules, inCustomerPanel, reattachCustomerToSystemRules, tab]);

    return (
        <>
            <div className={'shipping-fees-settings-view'}>
                <Col xs={12} className={'mt-3 mb-2 px-0'}>
                    <p className={'text-lg font-weight-bold'}>
                        {!inCustomerPanel ? "System" : defaultCustomer?.name ?? "Customer"} Free Shipping Management
                    </p>
                </Col>
                {
                    !inCustomerPanel &&
                    <Col xs={12} className={'mb-2 px-0'}>
                        <div className='w-100 flex-grow-1'>
                            <Tabs value={tab ?? false} onChange={onTabChanged}>
                                {
                                    tabsList.map(e => (
                                        <Tab key={e.value} value={e.value} label={e.label}/>
                                    ))
                                }
                            </Tabs>
                        </div>
                    </Col>
                }
                <Col xs={12} className={'px-0'}>
                    {
                        loading &&
                        <LoadingLottieAnimation width={'60%'}/>
                    }
                    {
                        !loading &&
                        content
                    }
                </Col>
            </div>
        </>
    )
}

export default FreeShippingFeesManagementView;
